/* oxlint-disable react/jsx-props-no-spreading -- props are intentionally forwarded by this adapter */
import { HTMLAttributes, ReactNode } from 'react'
import { Info } from 'app/components/Info'
import { cn } from 'app/lib/utils'

type SectionCardProps = HTMLAttributes<HTMLDivElement> & {
  title?: string
  children: ReactNode
  headerButtons?: ReactNode
  /** Overrides the body's padding and gap, e.g. `p-0 gap-0` for a table that runs edge to edge. */
  contentClassName?: string
  /** Draws the title as the card's own header row, inside the border, instead of above the card. */
  attached?: boolean
  /** An (i) beside an attached title that explains the card on hover. */
  info?: string
}

export function SectionCard({
  title,
  children,
  className,
  headerButtons,
  contentClassName,
  attached = false,
  info,
  ...rest
}: SectionCardProps) {
  if (attached) {
    return (
      <div
        className={cn(`flex flex-col overflow-hidden rounded-lg border border-default bg-surface-subtle`, className)}
        {...rest}
      >
        <div className="flex items-center justify-between gap-4 border-b border-default bg-surface px-4 py-3">
          {title && (
            <span className="flex items-center gap-1.5 text-body-md font-semibold">
              {title}
              {info && <Info title={title} message={info} className="h-4 w-4" />}
            </span>
          )}
          {headerButtons}
        </div>
        <div className={cn(`flex flex-col gap-6 px-4 py-4`, contentClassName)}>{children}</div>
      </div>
    )
  }

  return (
    <div className={`flex flex-col gap-4 ${className ?? ``}`} {...rest}>
      <div className="flex items-center justify-between px-1 pb-3">
        {title && <span className="text-body-md font-semibold">{title}</span>}
        {headerButtons}
      </div>
      <div
        className={cn(
          `flex flex-col gap-6 rounded-lg border border-default bg-surface-subtle px-4 py-4`,
          contentClassName,
        )}
      >
        {children}
      </div>
    </div>
  )
}
